import React, { useState, useEffect } from "react";
import { useAuth } from "../components/AuthContext";

const DOC_TYPE_LABELS = {
  BOL: { label: 'Bill of Lading', icon: '📄', color: '#3b82f6' },
  BILL_OF_LADING: { label: 'Bill of Lading', icon: '📄', color: '#3b82f6' },
  RAIL_BOL: { label: 'Rail Bill of Lading', icon: '🚂', color: '#6366f1' },
  RATE_CONFIRMATION: { label: 'Rate Confirmation', icon: '💰', color: '#10b981' },
  POD: { label: 'Proof of Delivery', icon: '✅', color: '#8b5cf6' },
  PROOF_OF_DELIVERY: { label: 'Proof of Delivery', icon: '✅', color: '#8b5cf6' },
  SWITCHING_ORDER: { label: 'Switching Order', icon: '🔄', color: '#f59e0b' },
  FREIGHT_INVOICE: { label: 'Freight Invoice', icon: '🧾', color: '#ef4444' },
  CARRIER_AGREEMENT: { label: 'Carrier Agreement', icon: '🤝', color: '#6366f1' },
  CUSTOMS_DECLARATION: { label: 'Customs Declaration', icon: '🛃', color: '#14b8a6' }
};

export default function BlockchainLedger() {
  const { adminEmail } = useAuth();
  const [activeTab, setActiveTab] = useState('documents');
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [auditTrail, setAuditTrail] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [docViewerData, setDocViewerData] = useState(null);
  const [docViewerLoading, setDocViewerLoading] = useState(false);
  const [generateType, setGenerateType] = useState('BOL');
  const [loadId, setLoadId] = useState('');
  const [loads, setLoads] = useState([]);

  const headers = { 'Content-Type': 'application/json', 'x-admin-email': adminEmail, 'x-user-role': 'BROKER_ADMIN' };

  useEffect(() => { loadData(); loadLoads(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [docsRes, statsRes] = await Promise.all([
        fetch('/api/blockchain/documents', { headers }).then(r => r.json()),
        fetch('/api/blockchain/stats', { headers }).then(r => r.json())
      ]);
      setDocuments(docsRes.documents || []);
      setStats(statsRes);
    } catch (e) { console.error('Load error:', e); }
    finally { setLoading(false); }
  }

  async function loadLoads() {
    try {
      const res = await fetch("/api/broker/loads", { headers });
      if (!res.ok) return;
      const data = await res.json();
      setLoads(data.loads || []);
    } catch (e) { console.error("Load loads error:", e); }
  }

  async function viewDocument(docId) {
    setDocViewerLoading(true);
    setShowDocViewer(true);
    try {
      const res = await fetch(`/api/blockchain/documents/${docId}`, { headers });
      const data = await res.json();
      setDocViewerData(data);
      setSelectedDoc(docId);
    } catch (e) { console.error('View error:', e); }
    finally { setDocViewerLoading(false); }
  }

  async function viewAuditTrail(docId) {
    try {
      const res = await fetch(`/api/blockchain/documents/${docId}`, { headers });
      const data = await res.json();
      setAuditTrail(data);
      setSelectedDoc(docId);
    } catch (e) { console.error('Audit trail error:', e); }
  }

  async function verifyDocument(docId) {
    try {
      const res = await fetch(`/api/blockchain/documents/${docId}/verify`, { headers });
      const data = await res.json();
      setVerifyResult(data);
    } catch (e) { console.error('Verify error:', e); }
  }

  async function signDocument(docId) {
    try {
      const res = await fetch(`/api/blockchain/documents/${docId}/sign`, { method: 'POST', headers, body: JSON.stringify({ role: 'Broker' }) });
      const data = await res.json();
      if (data.success) {
        loadData();
        if (showDocViewer && selectedDoc === docId) viewDocument(docId);
        if (activeTab === 'audit' && selectedDoc === docId) viewAuditTrail(docId);
      }
    } catch (e) { console.error('Sign error:', e); }
  }

  function downloadPdf(docId) {
    const url = `/api/blockchain/documents/${docId}/pdf`;
    window.open(url, '_blank');
  }

  async function generateDocument() {
    if (!loadId) return window.alert('Please select a load');
    try {
      const endpoint = generateType === 'BOL' ? '/api/blockchain/bol/generate' : '/api/blockchain/pod/generate';
      const res = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify({ loadId }) });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setShowGenerateModal(false);
      setLoadId('');
      loadData();
    } catch (e) { window.alert('Error: ' + e.message); }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading blockchain ledger...</div>;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 28 }}>⛓️</span> Blockchain Document Ledger
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: 14 }}>
            Immutable bills of lading, PODs, and audit trails
          </p>
        </div>
        <button onClick={() => setShowGenerateModal(true)} style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          ➕ Generate Document
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          <div style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', borderRadius: 12, padding: 20, color: 'white' }}>
            <div style={{ fontSize: 32, fontWeight: 900 }}>{stats.totalDocuments}</div>
            <div style={{ opacity: 0.8, fontSize: 13 }}>📄 Documents</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)', borderRadius: 12, padding: 20, color: 'white' }}>
            <div style={{ fontSize: 32, fontWeight: 900 }}>{stats.totalBlocks}</div>
            <div style={{ opacity: 0.8, fontSize: 13 }}>⛓️ Blocks</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)', borderRadius: 12, padding: 20, color: 'white' }}>
            <div style={{ fontSize: 32, fontWeight: 900 }}>{stats.totalSignatures}</div>
            <div style={{ opacity: 0.8, fontSize: 13 }}>✍️ Signatures</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)', borderRadius: 12, padding: 20, color: 'white' }}>
            <div style={{ fontSize: 32, fontWeight: 900 }}>{stats.chainVerified ? '✓' : '✗'}</div>
            <div style={{ opacity: 0.8, fontSize: 13 }}>🔒 Chain {stats.chainVerified ? 'Verified' : 'Invalid'}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
        {[{ id: 'documents', label: '📄 Documents' }, { id: 'audit', label: '📋 Audit Trail' }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding: '8px 16px', background: activeTab === tab.id ? '#3b82f6' : 'white',
            color: activeTab === tab.id ? 'white' : '#6b7280', border: '1px solid #e5e7eb',
            borderRadius: 8, fontWeight: 600, cursor: 'pointer'
          }}>{tab.label}</button>
        ))}
      </div>

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          {documents.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📄</div>
              <div>No blockchain documents yet. Generate a Bill of Lading to get started.</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Document</th>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Type</th>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Load Ref</th>
                  <th style={{ padding: 12, textAlign: 'center', fontSize: 12, color: '#6b7280' }}>Blocks</th>
                  <th style={{ padding: 12, textAlign: 'center', fontSize: 12, color: '#6b7280' }}>Signatures</th>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, color: '#6b7280' }}>Created</th>
                  <th style={{ padding: 12, textAlign: 'center', fontSize: 12, color: '#6b7280' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => {
                  const typeInfo = DOC_TYPE_LABELS[doc.documentType] || DOC_TYPE_LABELS[doc.type] || { label: doc.documentType || doc.type || 'Document', icon: '📄', color: '#6b7280' };
                  return (
                    <tr key={doc.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600 }}>{doc.documentNumber || doc.id.slice(-8)}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{(doc.contentHash || doc.blockchainHash || '').slice(0, 16)}...</div>
                      </td>
                      <td style={{ padding: 12 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: `${typeInfo.color}20`, color: typeInfo.color, borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                          {typeInfo.icon} {typeInfo.label}
                        </span>
                      </td>
                      <td style={{ padding: 12, fontSize: 13 }}>{doc.loadReference || doc.loadNumber || '-'}</td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>{doc.blockCount || 0}</span>
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <span style={{ background: '#d1fae5', color: '#059669', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>{doc.signatureCount || 0}</span>
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: '#6b7280' }}>{doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '-'}</td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button onClick={() => viewDocument(doc.id)} style={{ padding: '4px 8px', background: '#f3f4f6', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>View</button>
                          <button onClick={() => signDocument(doc.id)} style={{ padding: '4px 8px', background: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Sign</button>
                          <button onClick={() => verifyDocument(doc.id)} style={{ padding: '4px 8px', background: '#d1fae5', color: '#059669', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Verify</button>
                          <button onClick={() => downloadPdf(doc.id)} style={{ padding: '4px 8px', background: '#fef3c7', color: '#92400e', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>PDF</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Audit Trail Tab */}
      {activeTab === 'audit' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 16 }}>📄 Document Details</h3>
            {auditTrail ? (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Document Number</div>
                  <div style={{ fontWeight: 600 }}>{auditTrail.document.documentNumber}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Type</div>
                  <div style={{ fontWeight: 600 }}>{auditTrail.document.documentType}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Content Hash (SHA-256)</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all', background: '#f3f4f6', padding: 8, borderRadius: 4 }}>{auditTrail.document.contentHash}</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Created By</div>
                  <div style={{ fontWeight: 600 }}>{auditTrail.document.createdBy || 'System'}</div>
                </div>
                {auditTrail.document.shipment && (
                  <div style={{ background: '#f0f4ff', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#3b82f6', fontWeight: 700, marginBottom: 6 }}>Shipment Details</div>
                    <div style={{ fontSize: 13 }}>Load #: <strong>{auditTrail.document.shipment.loadNumber || 'N/A'}</strong></div>
                    <div style={{ fontSize: 13 }}>Route: {auditTrail.document.shipment.origin} → {auditTrail.document.shipment.destination}</div>
                    {auditTrail.document.shipment.commodity && <div style={{ fontSize: 13 }}>Commodity: {auditTrail.document.shipment.commodity}</div>}
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>Signatures ({auditTrail.signatures?.length || 0})</div>
                  {auditTrail.signatures?.map((sig, i) => (
                    <div key={i} style={{ fontSize: 12, padding: '4px 0' }}>✍️ {sig.signedBy} ({sig.role}) - {sig.timestamp ? new Date(sig.timestamp).toLocaleString() : 'N/A'}</div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button onClick={() => signDocument(selectedDoc)} style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✍️ Sign Document</button>
                  <button onClick={() => downloadPdf(selectedDoc)} style={{ padding: '8px 16px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>📥 Download PDF</button>
                </div>
              </>
            ) : (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>Select a document to view details</div>
            )}
          </div>

          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 16 }}>⛓️ Blockchain Trail</h3>
            {auditTrail?.auditTrail && auditTrail.auditTrail.length > 0 ? (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {auditTrail.auditTrail.map((block, i) => (
                  <div key={block.id} style={{ position: 'relative', paddingLeft: 24, paddingBottom: 16, borderLeft: i < auditTrail.auditTrail.length - 1 ? '2px solid #e5e7eb' : 'none' }}>
                    <div style={{ position: 'absolute', left: -6, top: 0, width: 12, height: 12, borderRadius: '50%', background: block.transactionType === 'SIGNED' ? '#10b981' : '#3b82f6' }} />
                    <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 13 }}>Block #{block.blockNumber}</span>
                        <span style={{ fontSize: 11, color: '#6b7280' }}>{block.timestamp ? new Date(block.timestamp).toLocaleString() : ''}</span>
                      </div>
                      <div style={{ fontSize: 12, marginBottom: 4 }}>
                        <span style={{ background: block.transactionType === 'SIGNED' ? '#d1fae5' : '#dbeafe', color: block.transactionType === 'SIGNED' ? '#059669' : '#1d4ed8', padding: '2px 6px', borderRadius: 4 }}>{block.transactionType}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>By: {block.signedBy}</div>
                      <div style={{ fontSize: 10, fontFamily: 'monospace', color: '#9ca3af', marginTop: 4 }}>Hash: {(block.blockHash || '').slice(0, 24)}...</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>
                {auditTrail ? 'No blockchain entries yet for this document' : 'Select a document to view blockchain trail'}
              </div>
            )}
          </div>

          {/* Quick select documents */}
          <div style={{ gridColumn: '1 / -1', background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 14 }}>Select a document:</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {documents.map(doc => (
                <button key={doc.id} onClick={() => viewAuditTrail(doc.id)} style={{
                  padding: '6px 12px', background: selectedDoc === doc.id ? '#3b82f6' : '#f3f4f6',
                  color: selectedDoc === doc.id ? 'white' : '#374151', border: 'none', borderRadius: 6,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer'
                }}>
                  {doc.documentNumber || doc.id.slice(-8)} ({doc.documentType || doc.type})
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ========== DOCUMENT VIEWER MODAL ========== */}
      {showDocViewer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowDocViewer(false)}>
          <div style={{ background: 'white', borderRadius: 16, width: 700, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            {docViewerLoading ? (
              <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading document...</div>
            ) : docViewerData ? (
              <>
                {/* Modal Header */}
                <div style={{ background: '#1e3a8a', color: 'white', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 18 }}>
                      {(DOC_TYPE_LABELS[docViewerData.document.documentType] || {}).icon || '📄'}{' '}
                      {(DOC_TYPE_LABELS[docViewerData.document.documentType] || {}).label || docViewerData.document.documentType}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>{docViewerData.document.documentNumber}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => signDocument(selectedDoc)} style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✍️ Sign</button>
                    <button onClick={() => downloadPdf(selectedDoc)} style={{ padding: '6px 14px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>📥 PDF</button>
                    <button onClick={() => setShowDocViewer(false)} style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}>✕</button>
                  </div>
                </div>

                {/* Modal Body */}
                <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
                  {/* Blockchain badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <span style={{ background: '#d1fae5', color: '#065f46', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>🔒 BLOCKCHAIN VERIFIED</span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Created: {docViewerData.document.createdAt ? new Date(docViewerData.document.createdAt).toLocaleString() : 'N/A'}</span>
                  </div>

                  {/* Shipment Info */}
                  {docViewerData.document.shipment && (
                    <div style={{ background: '#f0f4ff', border: '1px solid #dbeafe', borderRadius: 12, padding: 16, marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a8a', marginBottom: 12 }}>SHIPMENT DETAILS</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>Load Number</div>
                          <div style={{ fontWeight: 700 }}>{docViewerData.document.shipment.loadNumber || 'N/A'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>Status</div>
                          <div style={{ fontWeight: 700 }}>{docViewerData.document.shipment.status || 'N/A'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, marginTop: 16, alignItems: 'center' }}>
                        <div style={{ background: 'white', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>ORIGIN</div>
                          <div style={{ fontWeight: 700, fontSize: 14, marginTop: 4 }}>{docViewerData.document.shipment.origin || 'N/A'}</div>
                        </div>
                        <div style={{ fontSize: 24, color: '#3b82f6' }}>→</div>
                        <div style={{ background: 'white', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>DESTINATION</div>
                          <div style={{ fontWeight: 700, fontSize: 14, marginTop: 4 }}>{docViewerData.document.shipment.destination || 'N/A'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 12 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>Commodity</div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{docViewerData.document.shipment.commodity || 'N/A'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>Weight</div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{docViewerData.document.shipment.weightLbs ? `${Number(docViewerData.document.shipment.weightLbs).toLocaleString()} lbs` : 'N/A'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>Rate</div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{docViewerData.document.shipment.rateCents ? `$${(docViewerData.document.shipment.rateCents / 100).toLocaleString()}` : 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Document Content */}
                  {docViewerData.document.content && Object.keys(docViewerData.document.content).length > 0 && (
                    <div style={{ background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>DOCUMENT CONTENT</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        {Object.entries(docViewerData.document.content).map(([key, val]) => (
                          <div key={key}>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>{key}</div>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{String(val || 'N/A')}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Signatures */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>SIGNATURES ({docViewerData.signatures?.length || 0})</div>
                    {docViewerData.signatures && docViewerData.signatures.length > 0 ? (
                      docViewerData.signatures.map((sig, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #d1fae5', borderRadius: 8, marginBottom: 8 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 13 }}>✍️ {sig.signedBy}</div>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>{sig.role}</div>
                          </div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>{sig.timestamp ? new Date(sig.timestamp).toLocaleString() : ''}</div>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: '#9ca3af', fontSize: 13, fontStyle: 'italic' }}>No signatures yet — click Sign to add yours</div>
                    )}
                  </div>

                  {/* Hash */}
                  <div style={{ background: '#f0fdf4', border: '1px solid #d1fae5', borderRadius: 12, padding: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#065f46', marginBottom: 8 }}>BLOCKCHAIN VERIFICATION</div>
                    <div style={{ fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all', color: '#374151' }}>
                      SHA-256: {docViewerData.document.contentHash}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                      Document ID: {docViewerData.document.id}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ padding: 60, textAlign: 'center', color: '#ef4444' }}>Failed to load document</div>
            )}
          </div>
        </div>
      )}

      {/* Verify Result Modal */}
      {verifyResult && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setVerifyResult(null)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, width: 400 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>🔍 Verification Result</h3>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 64 }}>{verifyResult.verified ? '✅' : '❌'}</div>
              <div style={{ fontWeight: 700, fontSize: 18, marginTop: 8 }}>
                {verifyResult.verified ? 'Document Verified' : 'Verification Failed'}
              </div>
            </div>
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 12, fontSize: 13 }}>
              <div>Content Valid: {verifyResult.contentValid ? '✓' : '✗'}</div>
              <div>Chain Valid: {verifyResult.chainValid ? '✓' : '✗'}</div>
              <div>Block Count: {verifyResult.blockCount}</div>
              <div>Signatures: {verifyResult.signatures}</div>
            </div>
            <button onClick={() => setVerifyResult(null)} style={{ marginTop: 16, width: '100%', padding: 12, background: '#3b82f6', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowGenerateModal(false)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, width: 400 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>➕ Generate Blockchain Document</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Document Type</label>
              <select value={generateType} onChange={e => setGenerateType(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <option value="BOL">Bill of Lading</option>
                <option value="POD">Proof of Delivery</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Select Load</label>
              <select value={loadId} onChange={e => setLoadId(e.target.value)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                <option value="">-- Select a load --</option>
                {loads.map(load => (
                  <option key={load.id} value={load.id}>{load.referenceNumber || load.loadNumber || load.id.slice(-8)} - {load.originCity || '?'} to {load.destCity || '?'}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setShowGenerateModal(false)} style={{ flex: 1, padding: 12, background: '#f3f4f6', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={generateDocument} style={{ flex: 1, padding: 12, background: '#3b82f6', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Generate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
